import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Eye, EyeOff } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";

export default function Community() {
  const { profile } = useAuth();
  const pid = profile?.id;
  const [dist, setDist] = useState({ total: 0, distribution: [] });
  const [visible, setVisible] = useState(false);
  const myType = profile?.human_design?.type;

  const load = async () => {
    const { data } = await api.get("/community/types");
    setDist(data);
  };

  useEffect(() => {
    load();
    if (pid) api.get(`/profiles/${pid}/full`).then(({ data }) => setVisible(data.community_visibility));
  }, [pid]);

  if (profile === null) return <Navigate to="/setup" replace />;

  const toggle = async () => {
    const { data } = await api.patch(`/profiles/${pid}/community-visibility`);
    setVisible(data.community_visibility);
    toast.success(data.community_visibility ? "You're now part of the community stats" : "You're anonymous again");
    load();
  };

  const chartData = dist.distribution.length ? dist.distribution : [
    { type: "Generator", count: 0 }, { type: "Projector", count: 0 },
    { type: "Manifestor", count: 0 }, { type: "Reflector", count: 0 },
  ];

  return (
    <Shell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline mb-2">Anonymous · opt-in</p>
          <h1 className="font-serif text-5xl">Community</h1>
        </div>
        <button data-testid="visibility-toggle" onClick={toggle}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-border hover:border-primary/50 transition-colors text-sm">
          {visible ? <Eye size={16} className="text-primary" /> : <EyeOff size={16} />} {visible ? "Visible in stats" : "Join anonymously"}
        </button>
      </div>

      <div className="panel p-8">
        <p className="overline mb-1 flex items-center gap-2"><Users size={14} /> Human Design types</p>
        <p className="text-sm text-muted-foreground mb-6">{dist.total} companion{dist.total === 1 ? "" : "s"} sharing. You are a <span className="text-primary">{myType}</span>.</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="type" width={140} tick={{ fill: "hsl(240 10% 70%)", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {chartData.map((e) => <Cell key={e.type} fill={e.type === myType ? "hsl(43 50% 50%)" : "hsl(15 40% 45%)"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Shell>
  );
}
